import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { Analytics } from "@/lib/analytics";

const SESSION_KEY = "immovox_exit_popup_shown";
const DISMISS_KEY = "immovox_exit_popup_dismissed_at";
const DISMISS_DURATION_DAYS = 7;
const MIN_TIME_ON_SITE_MS = 5000;

const PUBLIC_ROUTES = [
  "/",
  "/fonctionnalites",
  "/tarifs",
  "/cas-dusage",
  "/a-propos",
  "/contact",
  "/blog",
];


const DEMO_PHONE = "05 17 94 84 03";
const DEMO_PHONE_TEL = "tel:+33517948403";

export default function ExitIntentPopup() {
  // Désactivé — conserver le code pour réactivation ultérieure
  return null;
  const [isOpen, setIsOpen] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  const isPublicRoute = useCallback(() => {
    const path = location.pathname;
    
    // Check if it's a public route or blog post
    return PUBLIC_ROUTES.includes(path) || path.startsWith("/blog/");
  }, [location.pathname]);

  const wasRecentlyDismissed = useCallback(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (!dismissedAt) return false;
    
    const dismissedDate = new Date(parseInt(dismissedAt, 10));
    const now = new Date();
    const daysSinceDismissed = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
    
    return daysSinceDismissed < DISMISS_DURATION_DAYS;
  }, []);

  const wasShownThisSession = useCallback(() => {
    return sessionStorage.getItem(SESSION_KEY) === "true";
  }, []);

  const canShow = useCallback(() => {
    // Don't show on mobile (no reliable exit intent detection)
    if (isMobile) return false;
    
    // Only show on public routes
    if (!isPublicRoute()) return false;
    
    // Don't show if already shown this session
    if (wasShownThisSession()) return false;
    
    // Don't show if recently dismissed
    if (wasRecentlyDismissed()) return false;
    
    return true;
  }, [isMobile, isPublicRoute, wasShownThisSession, wasRecentlyDismissed]);

  const showPopup = useCallback(() => {
    if (hasTriggered || !canShow()) return;
    
    setHasTriggered(true);
    setIsOpen(true);
    sessionStorage.setItem(SESSION_KEY, "true");
    Analytics.exitPopupShown();
  }, [hasTriggered, canShow]);

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    Analytics.exitPopupDismissed();
  }, []);

  const handlePhoneClick = useCallback(() => {
    Analytics.exitPopupPhoneClicked();
  }, []);

  useEffect(() => {
    // Don't setup listeners if we can't show anyway
    if (isMobile || hasTriggered) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    let canTrigger = false;

    // Wait minimum time before enabling exit intent
    timeoutId = setTimeout(() => {
      canTrigger = true;
    }, MIN_TIME_ON_SITE_MS);

    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse leaves through the top of the viewport
      if (e.clientY <= 0 && canTrigger) {
        showPopup();
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [isMobile, hasTriggered, showPopup]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleDismiss()}>
      <DialogContent className="sm:max-w-md border-border/50 bg-background/95 backdrop-blur-sm">
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Phone className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-2xl font-bold">
            Pas encore convaincu ?
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            Appelez Immovox et testez-le en conditions réelles.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex flex-col items-center gap-4">
          <Button
            asChild
            size="lg"
            className="w-full text-lg font-semibold"
            onClick={handlePhoneClick}
          >
            <a href={DEMO_PHONE_TEL}>
              <Phone className="mr-2 h-5 w-5" />
              {DEMO_PHONE}
            </a>
          </Button>
          
          <p className="text-xs text-muted-foreground">
            Appel non surtaxé, prix d'un appel local
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
